import AmazonButton from "./AmazonButton";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { optimizeBase44Image, squareThumbSrcSet } from "@/lib/imageOptimization";

const parseEfficiency = (str) => {
  if (!str) return 0;
  const match = String(str).match(/[\d.]+/);
  return match ? Math.min(parseFloat(match[0]), 25) : 0;
};

function EfficiencyBar({ value }) {
  const pct = parseEfficiency(value);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-accent" style={{ width: `${(pct / 25) * 100}%` }} />
      </div>
      <span className="font-mono font-bold text-xs text-accent-text whitespace-nowrap">{value || "—"}</span>
    </div>
  );
}

function Thumb({ product, className = "" }) {
  if (product.image_url) {
    return (
      <img
        src={optimizeBase44Image(product.image_url, 192, 192)}
        srcSet={squareThumbSrcSet(product.image_url)}
        sizes="64px"
        width="192"
        height="192"
        alt={`${product.product_name} — ${product.best_for_category} 2026`}
        className={`object-cover ${className}`}
        loading="lazy"
        decoding="async"
      />
    );
  }
  return (
    <div className={`flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 ${className}`}>
      <Zap className="w-5 h-5 text-accent" />
    </div>
  );
}

export default function ComparisonTable({ products }) {
  if (!products || products.length === 0) return null;

  return (
    <div className="w-full max-w-full rounded-2xl border border-border overflow-hidden shadow-lg">
      <div className="flex items-start gap-2.5 px-5 py-3.5 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200/60 dark:border-amber-800/30">
        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-400 leading-relaxed">
          Prices and availability change often. Use the Amazon button to check the current price and product details for each panel.
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block w-full max-w-full overflow-hidden">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-[5%]" />
            <col className="w-[24%]" />
            <col className="w-[19%]" />
            <col className="w-[14%]" />
            <col className="w-[8%]" />
            <col className="w-[14%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="bg-foreground text-background">
              <th className="py-3.5 px-3 text-center text-[10px] font-mono uppercase tracking-widest w-10">#</th>
              <th className="py-3.5 px-4 text-left text-[10px] font-mono uppercase tracking-widest">Product</th>
              <th className="py-3.5 px-3 text-left text-[10px] font-mono uppercase tracking-widest">Best For</th>
              <th className="py-3.5 px-3 text-center text-[10px] font-mono uppercase tracking-widest">Cell Type</th>
              <th className="py-3.5 px-3 text-center text-[10px] font-mono uppercase tracking-widest whitespace-nowrap">Watts</th>
              <th className="py-3.5 px-3 text-left text-[10px] font-mono uppercase tracking-widest">Efficiency</th>
              <th className="py-3.5 px-4 text-center text-[10px] font-mono uppercase tracking-widest">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products.map((product, index) => (
              <tr key={product.id || index} className="hover:bg-accent/5 transition-colors group">
                <td className="py-4 px-3 text-center">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-xs font-mono font-bold text-muted-foreground">{index + 1}</span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden border border-border shrink-0">
                      <Thumb product={product} className="w-full h-full" />
                    </div>
                    <div className="min-w-0">
                      <Link to={`/review/${product.review_slug}`} className="font-heading font-semibold text-sm group-hover:text-accent transition-colors line-clamp-2 break-words">
                        {product.product_name}
                      </Link>
                      {product.brand && <p className="text-xs text-muted-foreground mt-0.5">{product.brand}</p>}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-3">
                  <Badge className="max-w-full bg-accent/15 text-foreground border-accent/30 font-mono text-[10px] whitespace-normal text-center leading-tight font-semibold">{product.best_for_category}</Badge>
                </td>
                <td className="py-4 px-2 text-center">
                  <span className="text-sm font-medium break-words">{product.panel_type || "—"}</span>
                  {product.form_factor && <span className="block text-xs text-muted-foreground mt-0.5">{product.form_factor}</span>}
                </td>
                <td className="py-4 px-3 text-center whitespace-nowrap">
                  <span className="font-heading font-bold text-sm">{product.wattage ? `${product.wattage}W` : "—"}</span>
                </td>
                <td className="py-4 px-3 min-w-0">
                  <EfficiencyBar value={product.claimed_efficiency} />
                </td>
                <td className="py-4 px-4 text-center">
                  {product.amazon_url ? (
                    <AmazonButton url={product.amazon_url} productName={product.product_name} category={product.best_for_category} variant="compact" className="w-full !px-2 justify-center text-center leading-tight whitespace-normal" />
                  ) : (
                    <span className="text-[10px] font-mono text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded-md">Link needed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden divide-y divide-border">
        {products.map((product, index) => (
          <div key={product.id || index} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-xs font-mono font-bold text-muted-foreground shrink-0">{index + 1}</span>
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-border shrink-0">
                <Thumb product={product} className="w-full h-full" />
              </div>
              <div className="min-w-0 flex-1">
                <Link to={`/review/${product.review_slug}`} className="font-heading font-semibold text-sm hover:text-accent transition-colors line-clamp-2">{product.product_name}</Link>
                {product.brand && <p className="text-xs text-muted-foreground mt-0.5">{product.brand}</p>}
                <Badge className="mt-1.5 max-w-full bg-accent/15 text-foreground border-accent/30 font-mono text-[9px] whitespace-normal leading-tight font-semibold">{product.best_for_category}</Badge>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="bg-secondary/50 rounded-lg py-2 px-1">
                <p className="text-[9px] font-mono text-muted-foreground uppercase">Watts</p>
                <p className="font-heading font-bold text-sm">{product.wattage ? `${product.wattage}W` : "—"}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg py-2 px-1">
                <p className="text-[9px] font-mono text-muted-foreground uppercase">Cell</p>
                <p className="font-heading font-bold text-xs mt-0.5">{product.panel_type || "—"}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg py-2 px-1">
                <p className="text-[9px] font-mono text-muted-foreground uppercase">Eff.</p>
                <p className="font-heading font-bold text-sm text-accent-text">{product.claimed_efficiency || "—"}</p>
              </div>
            </div>
            {product.amazon_url ? (
              <AmazonButton url={product.amazon_url} productName={product.product_name} category={product.best_for_category} variant="compact" className="w-full" />
            ) : (
              <span className="text-[10px] font-mono text-amber-600">Link needed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}